import React, { lazy } from 'react';
import ReactDOM from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Provider } from "react-redux";
import { ToastContainer } from "react-toastify";
import App from './App.js';
import NotFound from './pages/NotFound.js';
import Login from './pages/Login.js';
import ForgotPassword from './pages/ForgotPassword.js';
import ResetPassword from './pages/ResetPassword.js';
import MfaVerify from './pages/MfaVerify.js';
import reportWebVitals from './reportWebVitals.js';
import { UserProvider } from './Contexts/LoginContext.js';
import { RoleProvider } from './Contexts/RoleContext.js';
import { FlashProvider } from './Contexts/FlashContext.jsx';
import { ThemeProvider as AppThemeProvider } from './Contexts/ThemeContext.jsx';
import { CurrencyProvider } from './Contexts/CurrencyContext.jsx';
import MuiThemeWrapper from './theme/MuiThemeWrapper.jsx';
import { EnquiryProvider } from './Contexts/EnquiryContext.js';
import { NotificationProvider } from './context/NotificationContext.jsx';
import { BonusBarProvider } from './context/BonusBarContext.jsx';
import { store } from "./store/store";
import ProtectedRoute from './pages/ProtectedRoute.js';
import AppLayout from './Components/AppLayout.jsx';
import AppointmentAttendanceToastWatcher from "./Components/AppointmentAttendanceToastWatcher.jsx";
import Users from './pages/Users.js';
import AppBuilderPage from './pages/admin/AppBuilderPage.jsx';
import AllEnquiries from './pages/AllEnquiries.js';
import FollowUps from './pages/FollowUps.js';
import UserProfile from './pages/UserProfile.js';
import ExhibitionEnquriries from './pages/ExhibitionEnquiries.js';
import VisaCounsellorAssignment from './pages/Assign_Visa_Counsellor.js';
import LogsPage from './pages/Log.js';
import Unauthorized from './pages/Unauthorised.js';
import ExhibitionForm from './pages/ExhibitionForm.js';
import AppointmentsMyPage from "./pages/AppointmentsMyPage.jsx";
import AppointmentsAllPage from "./pages/AppointmentsAllPage.jsx";
import AppointmentsDashboardPage from "./pages/AppointmentsDashboardPage.jsx";
import AppointmentsCalendarPage from "./pages/AppointmentsCalendarPage.jsx";
import AppointmentsSettingsPage from "./pages/AppointmentsSettingsPage.jsx";
import PublicBookingPage from "./pages/PublicBookingPage.jsx";
import UniversitiesListPage from "./pages/UniversitiesListPage.jsx";
import UniversitiesApprovalPage from "./pages/UniversitiesApprovalPage.jsx";
import UniversitiesReportsPage from "./pages/UniversitiesReportsPage.jsx";
import ProgramDetailPage from "./pages/ProgramDetailPage.jsx";
import CourseOverviewPage from "./pages/CourseOverviewPage.jsx";
import PromotionsPage from "./pages/PromotionsPage.jsx";
import AcademicsAnalyticsPage from "./pages/AcademicsAnalyticsPage.jsx";
import ScheduleCalendar from "./pages/ScheduleCalendar.jsx";
import ClassroomsPage from "./pages/ClassroomsPage.jsx";
import EventsListPage from "./pages/EventsListPage.jsx";
import CreateClassroomWizard from "./pages/CreateClassroomWizard.jsx";
import CreateEventWizard from "./pages/CreateEventWizard.jsx";
import EventApprovalsPage from "./pages/EventApprovalsPage.jsx";
import RescheduleRequestsPage from "./pages/RescheduleRequestsPage.jsx";
import EventProfilePage from "./pages/EventProfilePage.jsx";
import EventAccessStudent from "./pages/EventAccessStudent.jsx";
import EventScanner from "./pages/EventScanner.jsx";
import RegisterSchedulePage from "./pages/RegisterSchedulePage.jsx";
import LecturerAttendancePage from "./pages/LecturerAttendancePage.jsx";
import LecturerAttendanceOtpPage from "./pages/LecturerAttendanceOtpPage.jsx";
import AccountsPaymentsPage from "./pages/AccountsPaymentsPage.jsx";
import AccountsDashboard from "./pages/AccountsDashboard.jsx";
import AIChatPage from "./pages/AIChatPage.jsx";
import AIUsageDashboard from "./pages/AIUsageDashboard.jsx";
import WhatsAppInboxPage from "./pages/WhatsAppInboxPage.jsx";
import UnifiedInboxPage from "./pages/UnifiedInboxPage.jsx";
import PaymentPlanPage from "./pages/PaymentPlanPage.jsx";
import FinanceSettingsPage from "./pages/FinanceSettingsPage.jsx";
import ReportBuilderPage from "./pages/reports/ReportBuilderPage.jsx";
import BonusesPage from "./pages/bonuses/BonusesPage.jsx";
import BonusProfilePage from "./pages/bonuses/BonusProfilePage.jsx";
import CreateBonusPage from "./pages/bonuses/CreateBonusPage.jsx";
import BountyBoardPage from "./pages/bounties/BountyBoardPage.jsx";
import BountyProfilePage from "./pages/bounties/BountyProfilePage.jsx";
import TargetsPage from "./pages/targets/TargetsPage.jsx";
import TargetProfilePage from "./pages/targets/TargetProfilePage.jsx";
import CreateTargetPage from "./pages/targets/CreateTargetPage.jsx";
import EditTargetPage from "./pages/targets/EditTargetPage.jsx";
import AllCommissions from "./pages/commissions/AllCommissions.jsx";
import CommissionProfile from "./pages/commissions/CommissionProfile.jsx";
import CommissionStructureForm from "./pages/commissions/CommissionStructureForm.jsx";
import MyCommissions from "./pages/commissions/MyCommissions.jsx";
import IncentivesDashboard from "./pages/incentives/IncentivesDashboard.jsx";
import IncentivesBonusDetail from "./pages/incentives/IncentivesBonusDetail.jsx";
import IncentivesBountyDetail from "./pages/incentives/IncentivesBountyDetail.jsx";
import IncentivesCommissionDetail from "./pages/incentives/IncentivesCommissionDetail.jsx";
import IncentivesTargetsDetail from "./pages/incentives/IncentivesTargetsDetail.jsx";
import StaffPage from "./pages/staff/StaffPage.jsx";
import TasksPage from "./pages/TasksPage.jsx";
import CounsellorDashboard from "./pages/CounsellorDashboard.jsx";
import ApprovalsPage from "./pages/ApprovalsPage.jsx";
import "react-toastify/dist/ReactToastify.css";
import "./static/theme.css";
import "./static/Sidebar.css";

const CRMAnalyticsDashboard = lazy(() => import('./pages/dashboard/CRMAnalyticsDashboard.jsx'));
const BackOfficeDashboard = lazy(() => import('./pages/dashboard/BackOfficeDashboard.jsx'));
const NewEnquiries = lazy(() => import('./pages/NewEnquiries.js'));
const EnquiryProfile = lazy(() => import('./pages/EnquiryProfile.js'));
const EditEnquiry = lazy(() => import('./pages/EditEnquiry.js'));
const AllApplications = lazy(() => import('./pages/AllApplications.js'));
const CounsellorApplications = lazy(() => import('./pages/CounsellorApplications.js'));
const CreateEnquiryWizard = lazy(() => import('./pages/CreateEnquiryWizard.jsx'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage.jsx'));
const MarketingIntelligenceDashboard = lazy(() => import('./pages/marketing/MarketingIntelligenceDashboard.jsx'));
const CampaignManagementPage = lazy(() => import('./pages/marketing/CampaignManagementPage.jsx'));
const CampaignAnalyticsPage = lazy(() => import('./pages/marketing/CampaignAnalyticsPage.jsx'));
const RevenueAnalyticsPage = lazy(() => import('./pages/marketing/RevenueAnalyticsPage.jsx'));
const RevenueDrilldownPage = lazy(() => import('./pages/marketing/RevenueDrilldownPage.jsx'));
const CounsellorAnalyticsProfile = lazy(() => import('./pages/marketing/CounsellorAnalyticsProfile.jsx'));
const ProgramInsightsProfile = lazy(() => import('./pages/marketing/ProgramInsightsProfile.jsx'));
const CourseInsightsProfile = lazy(() => import('./pages/marketing/CourseInsightsProfile.jsx'));
const CampaignInsightsProfile = lazy(() => import('./pages/marketing/CampaignInsightsProfile.jsx'));
const MediumInsightsProfile = lazy(() => import('./pages/marketing/MediumInsightsProfile.jsx'));

// Define the router with routes
const router = createBrowserRouter([
  {
    path: "/",
    element: <Login />,
    errorElement: <NotFound />
  },
  {
    path: "/forgot-password",
    element: <ForgotPassword />
  },
  {
    path: "/reset-password",
    element: <ResetPassword />
  },
  {
    path: "/mfa-verify",
    element: <MfaVerify />
  },
  {
    path: "/exhibition-form",
    element: <ExhibitionForm />,
  },
  {
    path: "/lecturer-attendance/:token",
    element: <LecturerAttendanceOtpPage />,
  },
  {
    path: "/lecturer-attendance/:token/submit",
    element: <LecturerAttendancePage />,
  },
  {
    path: "/unauthorized",
    element: <Unauthorized />
  },
  {
    element: <AppLayout />,
    children: [
  {
    path: "tasks",
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Manager', 'Counsellor', 'Visa Counsellor', 'Receptionist', 'Back Office', 'Visa&Application Counsellor', 'Lecturer', 'Accounts', 'Accountant']}>
        <TasksPage />
      </ProtectedRoute>
    )
  },
  {
    path: "counsellor-dashboard",
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Counsellor', 'Visa Counsellor', 'Visa&Application Counsellor']}>
        <CounsellorDashboard />
      </ProtectedRoute>
    )
  },
  {
    path: "approvals",
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Manager', 'Lecturer', 'Accountant']}>
        <ApprovalsPage />
      </ProtectedRoute>
    )
  },
  {
    path: "exhibition-enquiries",
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Receptionist', 'Back Office']}>
        <ExhibitionEnquriries />
      </ProtectedRoute>
    )
  },
  {
    path: "home",
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Receptionist', 'Back Office']}>
        <CRMAnalyticsDashboard />
      </ProtectedRoute>
    )
  },
  {
    path: "back-office-dashboard",
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Back Office']}>
        <BackOfficeDashboard />
      </ProtectedRoute>
    )
  },
  {
    path: "create-enquiry",
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Receptionist', 'Back Office']}>
        <CreateEnquiryWizard />
      </ProtectedRoute>
    )
  },
  {
    path: "new-enquiry",
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Visa Counsellor', 'Counsellor', 'Visa&Application Counsellor']}>
        <NewEnquiries />
      </ProtectedRoute>
    )
  },
  {
    path: 'enquiry_profile',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Manager', 'Counsellor', 'Visa Counsellor', 'Receptionist', 'Back Office', 'Visa&Application Counsellor', 'Accountant', 'Accounts']}>
        <EnquiryProfile />
      </ProtectedRoute>
    ),
  },
  {
    path: 'edit_enquiry',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Counsellor', 'Visa Counsellor', 'Receptionist', 'Back Office', 'Visa&Application Counsellor', 'Accountant', 'Accounts']}>
        <EditEnquiry />
      </ProtectedRoute>
    ),
  },
  {
    path: 'marketing-intelligence',
    element: (
      <ProtectedRoute allowedRoles={['admin']}>
        <MarketingIntelligenceDashboard />
      </ProtectedRoute>
    ),
  },
  {
    path: 'marketing-intelligence/campaigns',
    element: (
      <ProtectedRoute allowedRoles={['admin']}>
        <CampaignManagementPage />
      </ProtectedRoute>
    ),
  },
  {
    path: 'marketing-intelligence/analytics',
    element: (
      <ProtectedRoute allowedRoles={['admin']}>
        <CampaignAnalyticsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: 'marketing-intelligence/revenue',
    element: (
      <ProtectedRoute allowedRoles={['admin']}>
        <RevenueAnalyticsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: 'marketing/revenue/:type',
    element: (
      <ProtectedRoute allowedRoles={['admin']}>
        <RevenueDrilldownPage />
      </ProtectedRoute>
    ),
  },
  {
    path: 'marketing/counsellor/:id',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Manager']}>
        <CounsellorAnalyticsProfile />
      </ProtectedRoute>
    ),
  },
  {
    path: 'marketing/program/:id',
    element: (
      <ProtectedRoute allowedRoles={['admin']}>
        <ProgramInsightsProfile />
      </ProtectedRoute>
    ),
  },
  {
    path: 'marketing/course/:id',
    element: (
      <ProtectedRoute allowedRoles={['admin']}>
        <CourseInsightsProfile />
      </ProtectedRoute>
    ),
  },
  {
    path: 'marketing/campaign/:id',
    element: (
      <ProtectedRoute allowedRoles={['admin']}>
        <CampaignInsightsProfile />
      </ProtectedRoute>
    ),
  },
  {
    path: 'marketing/medium/:id',
    element: (
      <ProtectedRoute allowedRoles={['admin']}>
        <MediumInsightsProfile />
      </ProtectedRoute>
    ),
  },
  {
    path: 'view-applications',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Manager', 'Counsellor', 'Visa Counsellor', 'Visa&Application Counsellor']}>
        <AllApplications />
      </ProtectedRoute>
    ),
  },
  {
    path: 'all-enquiry',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Manager', 'Counsellor', 'Visa Counsellor', 'Receptionist', 'Back Office', 'Visa&Application Counsellor', 'Accountant', 'Accounts']}>
        <AllEnquiries />
      </ProtectedRoute>
    ),
  },
  {
    path: 'followups',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Counsellor', 'Visa Counsellor', 'Receptionist', 'Back Office', 'Visa&Application Counsellor', 'Manager']}>
        <FollowUps />
      </ProtectedRoute>
    ),
  },
  {
    path: 'bonuses',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Manager']}>
        <BonusesPage />
      </ProtectedRoute>
    ),
  },
  {
    path: 'bounties',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Manager', 'Counsellor', 'Visa Counsellor', 'Receptionist', 'Back Office', 'Visa&Application Counsellor']}>
        <BountyBoardPage />
      </ProtectedRoute>
    ),
  },
  {
    path: 'bounties/:id',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Manager', 'Counsellor', 'Visa Counsellor', 'Receptionist', 'Back Office', 'Visa&Application Counsellor']}>
        <BountyProfilePage />
      </ProtectedRoute>
    ),
  },
  {
    path: 'bonuses/create',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Manager']}>
        <CreateBonusPage />
      </ProtectedRoute>
    ),
  },
  {
    path: 'bonuses/:id',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Manager', 'Counsellor', 'Visa Counsellor', 'Receptionist', 'Back Office', 'Visa&Application Counsellor']}>
        <BonusProfilePage />
      </ProtectedRoute>
    ),
  },
  {
    path: 'targets',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Manager', 'Counsellor', 'Visa Counsellor', 'Receptionist', 'Back Office', 'Visa&Application Counsellor']}>
        <TargetsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: 'targets/create',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Manager']}>
        <CreateTargetPage />
      </ProtectedRoute>
    ),
  },
  {
    path: 'targets/:id/edit',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Manager']}>
        <EditTargetPage />
      </ProtectedRoute>
    ),
  },
  {
    path: 'targets/:id',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Manager', 'Counsellor', 'Visa Counsellor', 'Receptionist', 'Back Office', 'Visa&Application Counsellor']}>
        <TargetProfilePage />
      </ProtectedRoute>
    ),
  },
  {
    path: 'commissions',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Manager']}>
        <AllCommissions />
      </ProtectedRoute>
    ),
  },
  {
    path: 'commissions/create',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Manager']}>
        <CommissionStructureForm />
      </ProtectedRoute>
    ),
  },
  {
    path: 'commissions/:id/edit',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Manager']}>
        <CommissionStructureForm />
      </ProtectedRoute>
    ),
  },
  {
    path: 'commissions/:id',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Manager', 'Counsellor', 'Visa Counsellor', 'Receptionist', 'Back Office', 'Visa&Application Counsellor']}>
        <CommissionProfile />
      </ProtectedRoute>
    ),
  },
  {
    path: 'my-commissions',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Manager', 'Counsellor', 'Visa Counsellor', 'Visa&Application Counsellor']}>
        <MyCommissions />
      </ProtectedRoute>
    ),
  },
  {
    path: 'incentives',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Manager', 'Counsellor', 'Visa Counsellor', 'Visa&Application Counsellor']}>
        <IncentivesDashboard />
      </ProtectedRoute>
    ),
  },
  {
    path: 'incentives/bonus',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Manager', 'Counsellor', 'Visa Counsellor', 'Visa&Application Counsellor']}>
        <IncentivesBonusDetail />
      </ProtectedRoute>
    ),
  },
  {
    path: 'incentives/bounty',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Manager', 'Counsellor', 'Visa Counsellor', 'Visa&Application Counsellor']}>
        <IncentivesBountyDetail />
      </ProtectedRoute>
    ),
  },
  {
    path: 'incentives/commission',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Manager', 'Counsellor', 'Visa Counsellor', 'Visa&Application Counsellor']}>
        <IncentivesCommissionDetail />
      </ProtectedRoute>
    ),
  },
  {
    path: 'incentives/targets',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Manager', 'Counsellor', 'Visa Counsellor', 'Visa&Application Counsellor']}>
        <IncentivesTargetsDetail />
      </ProtectedRoute>
    ),
  },
  {
    path: 'counsellor-applications',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Counsellor', 'Visa&Application Counsellor']}>
        <CounsellorApplications />
      </ProtectedRoute>
    )
  },
  {
    path: 'staff',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Manager']}>
        <StaffPage />
      </ProtectedRoute>
    ),
  },
  {
    path: 'users',
    element: (
      <ProtectedRoute allowedRoles={['admin']}>
        <Users />
      </ProtectedRoute>
    )
  },
  {
    path: 'admin/app-builder',
    element: (
      <ProtectedRoute allowedRoles={['admin']}>
        <AppBuilderPage />
      </ProtectedRoute>
    )
  },
  {
    path: 'userProfile',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Counsellor', 'Visa Counsellor', 'Receptionist', 'Back Office', 'Visa&Application Counsellor', 'Accounts', 'Accountant']}>
        <UserProfile />
      </ProtectedRoute>
    )
  },
  {
    path: 'assign-visa-counsellor',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Receptionist', 'Back Office']}>
        <VisaCounsellorAssignment />
      </ProtectedRoute>
    )
  },
  {
    path: 'server-logs',
    element: (
      <ProtectedRoute allowedRoles={['admin']}>
        <LogsPage />
      </ProtectedRoute>
    )
  },
  {
    path: 'notifications',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Counsellor', 'Visa Counsellor', 'Receptionist', 'Visa&Application Counsellor', 'Manager']}>
        <NotificationsPage />
      </ProtectedRoute>
    )
  },
  {
    path: "universities/list",
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Manager']}>
        <UniversitiesListPage />
      </ProtectedRoute>
    )
  },
  {
    path: "universities/program/:programId",
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Manager', 'Back Office']}>
        <ProgramDetailPage />
      </ProtectedRoute>
    )
  },
  {
    path: "programs/course-overview",
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Receptionist', 'Manager', 'Back Office', 'Counsellor', 'Visa Counsellor', 'Visa&Application Counsellor']}>
        <CourseOverviewPage />
      </ProtectedRoute>
    )
  },
  {
    path: "programs/analytics",
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Manager']}>
        <AcademicsAnalyticsPage />
      </ProtectedRoute>
    )
  },
  {
    path: "programs/promotions",
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Manager']}>
        <PromotionsPage />
      </ProtectedRoute>
    )
  },
  {
    path: "universities/approval",
    element: (
      <ProtectedRoute allowedRoles={['admin']}>
        <UniversitiesApprovalPage />
      </ProtectedRoute>
    )
  },
  {
    path: "universities/reports",
    element: (
      <ProtectedRoute allowedRoles={['admin']}>
        <UniversitiesReportsPage />
      </ProtectedRoute>
    )
  },
  {
    path: "appointments/my",
    element: (
      <ProtectedRoute allowedRoles={['Counsellor', 'Visa Counsellor', 'Visa&Application Counsellor']}>
        <AppointmentsMyPage />
      </ProtectedRoute>
    )
  },
  {
    path: "appointments/all",
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Manager']}>
        <AppointmentsAllPage />
      </ProtectedRoute>
    )
  },
  {
    path: "appointments/dashboard",
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Counsellor', 'Visa Counsellor', 'Receptionist', 'Visa&Application Counsellor', 'Manager']}>
        <AppointmentsDashboardPage />
      </ProtectedRoute>
    )
  },
  {
    path: "appointments/dashboard/:appointmentId",
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Counsellor', 'Visa Counsellor', 'Receptionist', 'Visa&Application Counsellor', 'Manager']}>
        <AppointmentsDashboardPage />
      </ProtectedRoute>
    )
  },
  {
    path: "appointments/calendar",
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Counsellor', 'Visa Counsellor', 'Receptionist', 'Visa&Application Counsellor', 'Manager']}>
        <AppointmentsCalendarPage />
      </ProtectedRoute>
    )
  },
  {
    path: "appointments/settings",
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Counsellor', 'Visa Counsellor', 'Receptionist', 'Visa&Application Counsellor', 'Manager']}>
        <AppointmentsSettingsPage />
      </ProtectedRoute>
    )
  },
  {
    path: "schedule",
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Manager', 'Counsellor', 'Visa Counsellor', 'Receptionist', 'Back Office', 'Visa&Application Counsellor', 'Lecturer']}>
        <ScheduleCalendar />
      </ProtectedRoute>
    )
  },
  {
    path: "schedule/classrooms/create",
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Manager', 'Counsellor', 'Visa Counsellor', 'Receptionist', 'Back Office', 'Visa&Application Counsellor', 'Lecturer']}>
        <CreateClassroomWizard />
      </ProtectedRoute>
    )
  },
  {
    path: "schedule/classrooms",
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Manager', 'Counsellor', 'Visa Counsellor', 'Receptionist', 'Back Office', 'Visa&Application Counsellor', 'Lecturer']}>
        <ClassroomsPage />
      </ProtectedRoute>
    )
  },
  {
    path: "schedule/events/create",
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Manager', 'Counsellor', 'Visa Counsellor', 'Receptionist', 'Back Office', 'Visa&Application Counsellor', 'Lecturer']}>
        <CreateEventWizard />
      </ProtectedRoute>
    )
  },
  {
    path: "schedule/events/:eventId",
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Manager', 'Counsellor', 'Visa Counsellor', 'Receptionist', 'Back Office', 'Visa&Application Counsellor', 'Lecturer']}>
        <EventProfilePage />
      </ProtectedRoute>
    )
  },
  {
    path: "schedule/events",
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Manager', 'Counsellor', 'Visa Counsellor', 'Receptionist', 'Back Office', 'Visa&Application Counsellor', 'Lecturer']}>
        <EventsListPage />
      </ProtectedRoute>
    )
  },
  {
    path: "schedule/approvals",
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Manager', 'Counsellor', 'Visa Counsellor', 'Receptionist', 'Back Office', 'Visa&Application Counsellor', 'Lecturer']}>
        <EventApprovalsPage />
      </ProtectedRoute>
    )
  },
  {
    path: "schedule/reschedule-requests",
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Manager']}>
        <RescheduleRequestsPage />
      </ProtectedRoute>
    )
  },
  {
    path: "event-access",
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Manager', 'Counsellor', 'Visa Counsellor', 'Receptionist', 'Back Office', 'Visa&Application Counsellor', 'Lecturer']}>
        <EventAccessStudent />
      </ProtectedRoute>
    ),
  },
  {
    path: "event-scan",
    element: (
      <ProtectedRoute allowedRoles={['admin', 'Manager', 'Counsellor', 'Visa Counsellor', 'Receptionist', 'Back Office', 'Visa&Application Counsellor', 'Lecturer']}>
        <EventScanner />
      </ProtectedRoute>
    ),
  },
  {
    path: "/register/:token",
    element: <RegisterSchedulePage />,
  },
  {
    path: "/book/:token",
    element: <PublicBookingPage />,
  },
  {
    path: "ai-chat",
    element: (
      <ProtectedRoute allowedRoles={["admin", "Counsellor", "Visa Counsellor", "Receptionist", "Back Office", "Visa&Application Counsellor", "Manager"]}>
        <AIChatPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "unified-inbox",
    element: (
      <ProtectedRoute allowedRoles={["admin", "Manager", "Counsellor", "Visa Counsellor", "Visa&Application Counsellor"]}>
        <UnifiedInboxPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "whatsapp-inbox",
    element: (
      <ProtectedRoute allowedRoles={["admin", "Manager", "Counsellor", "Visa Counsellor", "Visa&Application Counsellor"]}>
        <WhatsAppInboxPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "ai-usage",
    element: (
      <ProtectedRoute allowedRoles={["admin"]}>
        <AIUsageDashboard />
      </ProtectedRoute>
    ),
  },
  {
    path: "accounts-dashboard",
    element: (
      <ProtectedRoute allowedRoles={["admin", "Accounts", "Manager", "Accountant"]}>
        <AccountsDashboard />
      </ProtectedRoute>
    ),
  },
  {
    path: "accounts/payments",
    element: (
      <ProtectedRoute allowedRoles={["admin", "Accounts", "Manager", "Accountant"]}>
        <AccountsPaymentsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "accounts/payment-plan/:id",
    element: (
      <ProtectedRoute allowedRoles={["admin", "Accounts", "Manager", "Accountant"]}>
        <PaymentPlanPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "settings/finance",
    element: (
      <ProtectedRoute allowedRoles={["admin"]}>
        <FinanceSettingsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "reports",
    element: (
      <ProtectedRoute allowedRoles={["admin"]}>
        <ReportBuilderPage />
      </ProtectedRoute>
    ),
  },
    ],
  },
  {
    path: '/unauthorized',
    element: <Unauthorized />,
  },
  // {
  //   element: <LayoutWithIPCheck />, // Apply IP check to all below
  //   children: [
  //     {
  //       path: "/home",
  //       element: (
  //         <ProtectedRoute allowedRoles={['admin', 'Receptionist', 'Back Office']}>
  //           <Dashboard />
  //         </ProtectedRoute>
  //       )
  //     },
  //     {
  //       path: "/create-enquiry",
  //       element: (
  //         <ProtectedRoute allowedRoles={['admin', 'Receptionist', 'Back Office']}>
  //           <CreateEnquiry />
  //         </ProtectedRoute>
  //       )
  //     },
  //     {
  //       path: "/new-enquiry",
  //       element: (
  //         <ProtectedRoute allowedRoles={['admin', 'Visa Counsellor', 'Counsellor']}>
  //           <NewEnquiries />
  //         </ProtectedRoute>
  //       )
  //     },
  //    {
  //   path: '/enquiry_profile',
  //   element: (
  //     <ProtectedRoute allowedRoles={['admin', 'Counsellor', 'Visa Counsellor', 'Receptionist']}>
  //       <EnquiryProfile />
  //     </ProtectedRoute>
  //   ),
  // },
  // {
  //   path: '/edit_enquiry',
  //   element: (
  //     <ProtectedRoute allowedRoles={['admin', 'Counsellor', 'Visa Counsellor', 'Receptionist']}> 
  //       <EditEnquiry />
  //     </ProtectedRoute>
  //   ),
  // },  
  // {
  //   path: '/marketing-overview',
  //   element: (
  //     <ProtectedRoute allowedRoles={['admin']}>
  //       <MarketingOverview />
  //     </ProtectedRoute>
  //   ),
  // },
  // {
  //   path: '/view-applications',
  //   element: (
  //     <ProtectedRoute allowedRoles={[ 'admin', 'Counsellor', 'Visa Counsellor']}>
  //       <AllApplications />
  //     </ProtectedRoute>
  //   ),
  // },
  // {
  //   path: '/all-enquiry',
  //   element: (
  //     <ProtectedRoute allowedRoles={['admin', 'Counsellor', 'Visa Counsellor', 'Receptionist']}>
  //       <AllEnquiries />
  //     </ProtectedRoute>
  //   ),
  // },
  // {
  //   path: '/counsellor-applications',
  //   element: (
  //     <ProtectedRoute allowedRoles={['admin', 'Counsellor']}>
  //       <CounsellorApplications />
  //     </ProtectedRoute>
  //   )
  // },
  // {
  //   path: '/users',
  //   element: (
  //     <ProtectedRoute allowedRoles={['admin']}>
  //       <Users />
  //     </ProtectedRoute>
  //   )
  // },
  // {
  //   path: '/userProfile',
  //   element: (
  //     <ProtectedRoute allowedRoles={['admin']}>
  //       <UserProfile />
  //     </ProtectedRoute>
  //   )
  // },
  // {
  //   path: '/campaign_analytics',
  //   element: (
  //     <ProtectedRoute allowedRoles={['admin']}>
  //       <CampaignAnalytics />
  //     </ProtectedRoute>
  //   )
  // },
  // {
  //   path: '/medium-overview',
  //   element: (
  //     <ProtectedRoute allowedRoles={['admin']}>
  //       <MediumOverview />
  //     </ProtectedRoute>
  //   )
  // },
  // {
  //   path: '/assign-visa-counsellor',
  //   element: (
  //     <ProtectedRoute allowedRoles={['admin', 'Receptionist', 'Back Office']}>
  //       <VisaCounsellorAssignment />
  //     </ProtectedRoute>
  //   )
  // },
  // {
  //   path: '/advertising-analysis',
  //   element: (
  //     <ProtectedRoute allowedRoles={['admin']}>
  //       <MarketingAnalysis />
  //     </ProtectedRoute>
  //   )
  // },
  // {
  //   path: '/marketing-counsellor-performance',
  //   element: (
  //     <ProtectedRoute allowedRoles={['admin']}>
  //       <CounsellorAnalytics />
  //     </ProtectedRoute>
  //   )
  // },
  // {
  //   path: '/server-logs',
  //   element: (
  //     <ProtectedRoute allowedRoles={['admin']}>
  //       <LogsPage />
  //     </ProtectedRoute>
  //   )
  // },
  // {
  //   path: '/unauthorized',
  //   element: <Unauthorized />,
  // },
  //   ]
  // }
]);


const isDev = process.env.NODE_ENV === "development";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: isDev ? 0 : 60 * 1000,
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
});

// Create the root element and render
const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
    <Provider store={store}>
      <AppThemeProvider>
      <MuiThemeWrapper>
      <UserProvider>
        <RoleProvider>
          <CurrencyProvider>
          <NotificationProvider>
            <EnquiryProvider>
              <FlashProvider>
              <BonusBarProvider>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: '100vh', // ensures full viewport height
              }}>
                <div style={{ flex: 1 }}>
                  <RouterProvider router={router} />
                <AppointmentAttendanceToastWatcher />
                <ToastContainer position="top-right" autoClose={5000} newestOnTop />
                </div>
                <footer style={{
                  textAlign: 'center',
                  backgroundColor: 'var(--theme-footer-bg)',
                  width: '100%',
                  padding: '0 0',
                  margin: '0',
                  position: 'relative',
                }}>
                  <p style={{ color: 'var(--theme-footer-text)', fontSize: '0.8rem' }}>© {new Date().getFullYear()} CrunchTime</p>
                </footer>
              </div>
              </BonusBarProvider>
              </FlashProvider>
            </EnquiryProvider>
          </NotificationProvider>
          </CurrencyProvider>
        </RoleProvider>
      </UserProvider>
      </MuiThemeWrapper>
      </AppThemeProvider>
    </Provider>
    </QueryClientProvider>
  </React.StrictMode>
);


reportWebVitals();

if (typeof navigator !== "undefined" && "serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {});
  });
}
